import { useState } from "react";
import { Check, ExternalLink, Phone, Radio } from "lucide-react";

import { ApiError } from "../../lib/api";
import { formatAbsolute, formatTooltip } from "../../lib/datetime";
import { buildLiveKitMeetUrl, type DispatchResponse } from "../../api/dispatch";
import { useDispatch } from "../../hooks/useDispatch";
import { FormField, inputStyle } from "../../components/ui/FormField";
import { Sheet } from "../../components/Sheet";

interface Props {
  open: boolean;
  engagementId: string;
  engagementName: string;
  allowedPhones: string[];
  onClose: () => void;
}

/**
 * Two-step dispatch modal.
 *   1. Operator picks a target phone from the engagement allowlist + confirms
 *   2. Backend returns the dispatch bundle; show room + monitor link + token
 *
 * The moment-of-truth UX before every call — designed to feel committed +
 * accountable: amber accent (callsign for outbound action), mono mission-
 * brief feel, copy-to-clipboard confirmations.
 */
export function DispatchCallModal({
  open,
  engagementId: _engagementId,
  engagementName,
  allowedPhones,
  onClose,
}: Props) {
  void _engagementId; // kept in API surface; engagementName carries the human label
  const [picked, setPicked] = useState<string>("");
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<DispatchResponse | null>(null);
  const [copyOk, setCopyOk] = useState(false);

  const dispatch = useDispatch(_engagementId);

  function reset() {
    setPicked("");
    setError(null);
    setResult(null);
    setCopyOk(false);
    dispatch.reset();
  }

  function handleClose() {
    reset();
    onClose();
  }

  async function confirmDispatch() {
    if (!picked) {
      setError("Pick a target phone first.");
      return;
    }
    setError(null);
    try {
      const r = await dispatch.mutateAsync(picked);
      setResult(r);
    } catch (e) {
      if (e instanceof ApiError) {
        setError(`Server error ${e.status}: ${e.detail}`);
      } else if (e instanceof Error) {
        setError(e.message);
      } else {
        setError("Dispatch failed — unknown error");
      }
    }
  }

  async function copyToken() {
    if (!result) return;
    try {
      await navigator.clipboard.writeText(result.operator_access_token);
      setCopyOk(true);
      setTimeout(() => setCopyOk(false), 2000);
    } catch {
      setError("Clipboard write failed — copy the token manually");
    }
  }

  return (
    <Sheet open={open} onClose={handleClose}>
      <div style={{ position: "relative", fontFamily: "var(--font-sans)" }}>
        {/* top accent line — amber for dispatch */}
        <span
          aria-hidden
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            height: 1,
            background:
              "linear-gradient(90deg, transparent, var(--cta), transparent)",
          }}
        />

        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            marginBottom: 16,
          }}
        >
          <div>
            <div
              style={{
                fontFamily: "var(--font-mono)",
                fontSize: 10,
                letterSpacing: "0.20em",
                color: "var(--cta)",
                marginBottom: 6,
                display: "flex",
                alignItems: "center",
                gap: 8,
              }}
            >
              <Radio size={11} strokeWidth={2} />
              {result ? "DISPATCH ACKNOWLEDGED" : "DISPATCH CALL"}
            </div>
            <h3
              style={{
                margin: 0,
                fontFamily: "var(--font-display)",
                fontSize: 22,
                fontWeight: 600,
                letterSpacing: "-0.01em",
                color: "var(--text-primary)",
                lineHeight: 1.2,
              }}
            >
              {engagementName}
            </h3>
          </div>
        </div>

        {!result ? (
          // ── Step 1: pick target phone ────────────────────────────────
          <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            <FormField label="Target phone · from allowlist">
              {allowedPhones.length === 0 ? (
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 10,
                    fontFamily: "var(--font-mono)",
                    fontSize: 11,
                    letterSpacing: "0.06em",
                    color: "var(--error)",
                    background: "rgba(244,63,94,0.05)",
                    border: "1px dashed var(--error)",
                    borderRadius: "var(--radius-input)",
                    padding: "11px 12px",
                  }}
                >
                  <Phone size={12} strokeWidth={1.5} />
                  No phones in the engagement allowlist. Import or add one first.
                </div>
              ) : (
                <select
                  value={picked}
                  onChange={(e) => setPicked(e.target.value)}
                  style={{
                    ...inputStyle,
                    fontFamily: "var(--font-mono)",
                    letterSpacing: "0.04em",
                  }}
                >
                  <option value="">— pick a target phone —</option>
                  {allowedPhones.map((p) => (
                    <option key={p} value={p}>
                      {p}
                    </option>
                  ))}
                </select>
              )}
            </FormField>

            {error ? <ErrorRow message={error} /> : null}

            <p
              style={{
                margin: 0,
                fontSize: 11.5,
                color: "var(--text-muted)",
                lineHeight: 1.6,
                letterSpacing: "-0.005em",
              }}
            >
              Dispatch creates a LiveKit room and asks the voice worker to join it.
              A short-TTL operator access token is returned so you can monitor the
              call in a separate tab.
            </p>

            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
                gap: 8,
                paddingTop: 12,
                borderTop: "1px solid var(--border-subtle)",
              }}
            >
              <button onClick={handleClose} style={ghostButtonStyle}>
                CANCEL
              </button>
              <button
                onClick={() => void confirmDispatch()}
                disabled={
                  !picked || dispatch.isPending || allowedPhones.length === 0
                }
                style={
                  !picked || dispatch.isPending || allowedPhones.length === 0
                    ? primaryButtonDisabled
                    : primaryButtonEnabled
                }
              >
                {dispatch.isPending ? "DISPATCHING…" : "DISPATCH"}
              </button>
            </div>
          </div>
        ) : (
          // ── Step 2: success / handoff ─────────────────────────────────
          <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            {/* mission brief — handoff details */}
            <div
              style={{
                background: "var(--bg-tertiary)",
                border: "1px solid var(--border-subtle)",
                borderLeft: "2px solid var(--cta)",
                borderRadius: "var(--radius-input)",
                padding: "14px 16px",
                display: "grid",
                gridTemplateColumns: "120px 1fr",
                gap: "10px 14px",
                fontFamily: "var(--font-mono)",
                fontSize: 11,
                letterSpacing: "0.02em",
                alignItems: "baseline",
              }}
            >
              <Label>CALL ID</Label>
              <Value mono>{result.call_id}</Value>
              <Label>ROOM</Label>
              <Value mono>{result.room_name}</Value>
              <Label>TOKEN EXPIRES</Label>
              <Value>
                <span title={formatTooltip(result.expires_at)}>
                  {formatAbsolute(result.expires_at)}{" "}
                  <span style={{ color: "var(--text-muted)" }}>
                    (in {Math.max(
                      0,
                      Math.round(
                        (new Date(result.expires_at).getTime() - Date.now()) / 60000,
                      ),
                    )}m)
                  </span>
                </span>
              </Value>
            </div>

            <p
              style={{
                margin: 0,
                fontSize: 11.5,
                color: "var(--text-muted)",
                lineHeight: 1.6,
              }}
            >
              The worker is being asked to join the room now. Open the monitor to
              listen in via LiveKit Meet. The token is room-scoped and expires shortly.
            </p>

            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                gap: 10,
                paddingTop: 12,
                borderTop: "1px solid var(--border-subtle)",
                flexWrap: "wrap",
              }}
            >
              <button onClick={() => void copyToken()} style={ghostButtonStyle}>
                {copyOk ? (
                  <>
                    <Check size={12} strokeWidth={2} /> COPIED
                  </>
                ) : (
                  "COPY TOKEN"
                )}
              </button>
              <div style={{ display: "flex", gap: 8 }}>
                <button onClick={handleClose} style={ghostButtonStyle}>
                  CLOSE
                </button>
                <a
                  href={buildLiveKitMeetUrl(
                    result.livekit_url,
                    result.operator_access_token,
                  )}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    ...primaryButtonEnabled,
                    background: "var(--accent)",
                    borderColor: "var(--accent)",
                    color: "#04181A",
                    textDecoration: "none",
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 8,
                  }}
                >
                  OPEN MONITOR
                  <ExternalLink size={12} strokeWidth={2} />
                </a>
              </div>
            </div>
          </div>
        )}
      </div>
    </Sheet>
  );
}

function Label({ children }: { children: React.ReactNode }) {
  return (
    <span
      style={{
        fontFamily: "var(--font-mono)",
        fontSize: 9.5,
        letterSpacing: "0.18em",
        color: "var(--text-muted)",
      }}
    >
      {children}
    </span>
  );
}

function Value({ children, mono }: { children: React.ReactNode; mono?: boolean }) {
  return (
    <span
      style={{
        color: "var(--text-primary)",
        fontFamily: mono ? "var(--font-mono)" : "var(--font-sans)",
        fontSize: mono ? 11 : 12,
        wordBreak: "break-all",
      }}
    >
      {children}
    </span>
  );
}

function ErrorRow({ message }: { message: string }) {
  return (
    <div
      role="alert"
      style={{
        fontFamily: "var(--font-mono)",
        fontSize: 11,
        letterSpacing: "0.04em",
        color: "var(--error)",
        background: "rgba(244,63,94,0.07)",
        border: "1px solid var(--error)",
        borderRadius: "var(--radius-input)",
        padding: "9px 12px",
      }}
    >
      {message}
    </div>
  );
}

const ghostButtonStyle: React.CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  gap: 6,
  padding: "10px 14px",
  minHeight: "var(--tap-min)",
  borderRadius: "var(--radius-input)",
  border: "1px solid var(--border)",
  background: "transparent",
  color: "var(--text-secondary)",
  cursor: "pointer",
  fontFamily: "var(--font-mono)",
  fontSize: 10.5,
  letterSpacing: "0.14em",
  fontWeight: 500,
};

const primaryButtonEnabled: React.CSSProperties = {
  padding: "10px 18px",
  minHeight: "var(--tap-min)",
  borderRadius: "var(--radius-input)",
  background: "var(--cta)",
  color: "#1A1108",
  border: "1px solid var(--cta)",
  cursor: "pointer",
  fontFamily: "var(--font-mono)",
  fontSize: 10.5,
  letterSpacing: "0.16em",
  fontWeight: 600,
  boxShadow: "var(--glow-cta)",
};

const primaryButtonDisabled: React.CSSProperties = {
  padding: "10px 18px",
  minHeight: "var(--tap-min)",
  borderRadius: "var(--radius-input)",
  background: "var(--bg-tertiary)",
  color: "var(--text-muted)",
  border: "1px solid var(--border-subtle)",
  cursor: "not-allowed",
  fontFamily: "var(--font-mono)",
  fontSize: 10.5,
  letterSpacing: "0.16em",
  fontWeight: 500,
};
